import { Rectangle } from 'pixi.js';
import type { TextMetrics } from '../metrics/TextMetrics';
import {
    calculateRadiusAfterFit,
    getHalfDiagonal,
    getTotalBounds,
    isBreakingSpace,
} from '../textMetricsHelpers';
import {
    DynamicInferredDimensions,
    DynamicLevel,
    DynamicXAlign,
    DynamicYAlign,
} from '../../../../enums';
import type { Pixels, RgbaString } from '../../../../types';
import { Config } from '../../../../video-data/config';
import { rgbArray2String, string2rgbaArray } from '../../../../libs/videoData';
import { createLinearGradient } from '../../../../libs/gradient';
import type { GradientPointCoords } from '../../../../libs/gradient';
import type {
    TextDynamicGraphicRendererSettingsBase,
    Position,
} from './TextDynamicGraphicRenderer';
import {
    WORD_LEVEL_ALIGN_Y_OPTIONS,
    TextDynamicGraphicRenderer,
} from './TextDynamicGraphicRenderer';
import type { TextDynamicGraphicAnimationRenderer } from './TextDynamicGraphicAnimationRenderer';

type CircleParameters = Position & {
    radius: Pixels;
    lineWidth: Pixels;
};

export interface TextDynamicCircleRendererSettings extends TextDynamicGraphicRendererSettingsBase {
    type: typeof Config.CIRCLE_GRAPHIC;
    radius: Pixels;
    // The gap kept between the text and the circle edge; `null` until the asset captures it.
    radiusPadding: Pixels | null;
    // The radius the circle settles at ignoring the fit; `null` while the circle isn't fitting.
    radiusBeforeFit: Pixels | null;
    strokeStyle: RgbaString;
    lineWidth: Pixels;
    fill: RgbaString;

    // gradient fill
    fillType: 'solid' | 'linear';
    gradientStartStop: RgbaString;
    gradientEndStop: RgbaString;
    gradientStartPoint: GradientPointCoords;
    gradientEndPoint: GradientPointCoords;
}

type AnimatableCircleProperties = (typeof TextDynamicCircleRenderer.animatableProperties)[number];
export enum AnimatableGeometricCircleProperties {
    radius = 'radius',
}

/**
 * Renders a circle behind a block of text.
 */
export class TextDynamicCircleRenderer extends TextDynamicGraphicRenderer {
    override readonly type = Config.CIRCLE_GRAPHIC;
    radius = 0 as Pixels;
    #radiusAfterFit: Pixels | null = null;
    radiusPadding: Pixels | null = null;
    radiusBeforeFit: Pixels | null = null;
    strokeStyle = 'rgba(255,255,255,0)' as RgbaString;
    lineWidth = 0 as Pixels;
    fill: RgbaString = 'rgba(255,0,255,1)';
    fillType: 'solid' | 'linear' = 'solid';
    gradientStartStop: RgbaString = 'rgba(255,0,0,1)';
    gradientEndStop: RgbaString = 'rgba(0,0,255,1)';
    gradientStartPoint: GradientPointCoords = { x: 0, y: 0.5 };
    gradientEndPoint: GradientPointCoords = { x: 1, y: 0.5 };

    /** Circles are position relative to their center. */
    #centerPositionsForLevel: Position[] = [];

    override animationConfigurations: TextDynamicGraphicAnimationRenderer<AnimatableCircleProperties>[] =
        [];

    override apply(settings: TextDynamicCircleRendererSettings) {
        super.apply(settings);
        this.radius = settings.radius;
        this.radiusPadding = settings.radiusPadding;
        this.radiusBeforeFit = settings.radiusBeforeFit;
        this.strokeStyle = settings.strokeStyle;
        this.lineWidth = settings.lineWidth;
        this.fill = settings.fill;
        this.fillType = settings.fillType;
        this.gradientStartStop = settings.gradientStartStop;
        this.gradientEndStop = settings.gradientEndStop;
        this.gradientStartPoint = settings.gradientStartPoint;
        this.gradientEndPoint = settings.gradientEndPoint;
    }

    #createLinearGradient(
        context: CanvasRenderingContext2D,
        center: Position,
        radius: number,
    ): CanvasGradient {
        // The gradient points are normalized to the circle's bounding box (a 2*radius square).
        const diameter = radius * 2;
        const left = center.x - radius;
        const top = center.y - radius;
        return createLinearGradient(context, {
            startX: left + this.gradientStartPoint.x * diameter,
            startY: top + this.gradientStartPoint.y * diameter,
            endX: left + this.gradientEndPoint.x * diameter,
            endY: top + this.gradientEndPoint.y * diameter,
            startStop: this.gradientStartStop,
            endStop: this.gradientEndStop,
        });
    }

    /**
     * Builds up a collection of center positions, one for each circle
     */
    override build(metrics: TextMetrics) {
        super.build(metrics);

        this.#radiusAfterFit = null;
        switch (this.level) {
            case DynamicLevel.object:
                this.#radiusAfterFit = this.#getObjectLevelRadiusAfterFit(metrics);
                this.#centerPositionsForLevel = this.#getObjectLevelPosition(metrics);
                break;
            case DynamicLevel.line:
                this.#centerPositionsForLevel = this.#getLineLevelPositions(metrics);
                break;
            case DynamicLevel.word:
                this.#centerPositionsForLevel = this.#getWordLevelPositions(metrics);
                break;
            case DynamicLevel.highlight:
                this.#centerPositionsForLevel = this.#getHighlightLevelPositions(metrics);
                break;
            default:
                throw new Error(`Invalid level ${this.level}`);
        }
    }

    #getObjectLevelRadiusAfterFit(metrics: TextMetrics): Pixels | null {
        if (this.radiusPadding === null) {
            if (this.radiusBeforeFit === null) return null;
            return Math.max(0, this.radiusBeforeFit) as Pixels;
        }
        return calculateRadiusAfterFit(
            getHalfDiagonal(metrics.bounds),
            this.radiusPadding,
        ) as Pixels;
    }

    #getObjectLevelPosition(metrics: TextMetrics): Position[] {
        return [
            {
                x: this.#getXPosition(metrics.bounds) + this.positionX,
                y: this.#getYPosition(metrics.bounds, metrics.ascent) + this.positionY,
            },
        ];
    }

    #getLineLevelPositions(metrics: TextMetrics): Position[] {
        return metrics.lines.map((line) => {
            return {
                x: this.#getXPosition(line.bounds) + this.positionX,
                y: this.#getYPosition(line.bounds, line.ascent) + this.positionY,
            };
        });
    }

    #getWordLevelPositions(metrics: TextMetrics): Position[] {
        const positions: Position[] = [];
        metrics.lines.forEach((line) => {
            line.words.forEach((word) => {
                if (isBreakingSpace(word.text)) {
                    return;
                }
                let boundsForY: Rectangle;
                if (WORD_LEVEL_ALIGN_Y_OPTIONS.includes(this.alignY)) {
                    // the unique word level alignment options use the line bounds instead
                    boundsForY = line.bounds;
                } else {
                    boundsForY = word.bounds;
                }
                positions.push({
                    x: this.#getXPosition(word.bounds) + this.positionX,
                    y: this.#getYPosition(boundsForY, line.ascent) + this.positionY,
                });
            });
        });

        return positions;
    }

    #getHighlightLevelPositions(metrics: TextMetrics): Position[] {
        const positions: Position[] = [];
        metrics.lines.forEach((line) => {
            line.highlightSpans.forEach((span) => {
                let boundsForY: Rectangle;
                if (WORD_LEVEL_ALIGN_Y_OPTIONS.includes(this.alignY)) {
                    boundsForY = line.bounds;
                } else {
                    boundsForY = span.bounds;
                }
                positions.push({
                    x: this.#getXPosition(span.bounds) + this.positionX,
                    y: this.#getYPosition(boundsForY, line.ascent) + this.positionY,
                });
            });
        });

        return positions;
    }

    #getXPosition({ x, width }: Rectangle): number {
        // The text bounds are relative to the top left while the circle is relative to its center.
        const circleTotalRadius = this.#drawnRadiusWithStroke();
        switch (this.alignX) {
            case DynamicXAlign.left:
                x += circleTotalRadius;
                break;
            case DynamicXAlign.center:
                x += width * 0.5;
                break;
            case DynamicXAlign.right:
                x += width - circleTotalRadius;
                break;
        }
        return x;
    }

    #getYPosition({ y, height }: Rectangle, ascent: number): number {
        // The text bounds are relative to the top left while the circle is relative to its center.
        const circleTotalRadius = this.#drawnRadiusWithStroke();
        switch (this.alignY) {
            case DynamicYAlign.maxTop:
            case DynamicYAlign.top:
                y += circleTotalRadius;
                break;
            case DynamicYAlign.center:
            case DynamicYAlign.averageCenter:
                y += height * 0.5;
                break;
            case DynamicYAlign.baseline:
                y += ascent - circleTotalRadius;
                break;
            case DynamicYAlign.bottom:
            case DynamicYAlign.maxBottom:
                y += height - circleTotalRadius;
                break;
        }
        return y;
    }

    /**
     * The radius defines the size of the circle, but then an outline is drawn on the circumference of that
     * circle using the line width. The line width is drawn half inside the circle and half outside the circle.
     * This means that the total radius of the circle is the radius plus half the line width.
     */
    #authoredRadiusWithStroke(): number {
        return this.radius + this.lineWidth / 2;
    }

    #drawnRadius(): number {
        return this.#radiusAfterFit ?? this.radius;
    }

    /** Stretches a radius the animation produced so the curve lands on the fitted radius. */
    #fitAnimatedRadius(animatedRadius: number): Pixels {
        if (this.#radiusAfterFit === null) return animatedRadius as Pixels;
        const radiusBeforeFit = this.radiusBeforeFit ?? this.radius;
        // A circle that rests at nothing has no curve to stretch, so it draws the fitted radius outright.
        if (radiusBeforeFit === 0) return this.#radiusAfterFit;
        return (animatedRadius * (this.#radiusAfterFit / radiusBeforeFit)) as Pixels;
    }

    #drawnRadiusWithStroke(): number {
        return this.#drawnRadius() + this.lineWidth / 2;
    }

    override _getBounds() {
        const length = 2 * this.#drawnRadius() + this.lineWidth;
        const halfLength = length / 2;
        return getTotalBounds(
            this.#centerPositionsForLevel.map(
                ({ x, y }) => new Rectangle(x - halfLength, y - halfLength, length, length),
            ),
        );
    }

    static get animatableProperties() {
        return [
            ...TextDynamicGraphicRenderer.baseAnimatableProperties,
            ...Object.values(AnimatableGeometricCircleProperties),
        ];
    }

    static get animatableGeometricProperties() {
        return [
            ...TextDynamicGraphicRenderer.baseAnimatableGeometricProperties,
            ...Object.values(AnimatableGeometricCircleProperties),
        ];
    }

    override draw(
        context: CanvasRenderingContext2D,
        inclusionFilterByWordIndex: ((ind: number) => boolean) | undefined,
    ) {
        this.#centerPositionsForLevel.forEach((_, i) => {
            if (inclusionFilterByWordIndex && !inclusionFilterByWordIndex(i)) {
                return;
            }
            const [position, radius, fill] = this._applyAnimationToIndex(i);
            const fillStyle =
                this.fillType === 'linear'
                    ? this.#createLinearGradient(context, position, radius)
                    : fill;
            drawCircle(
                context,
                { x: position.x, y: position.y, radius, lineWidth: this.lineWidth },
                fillStyle,
                this.strokeStyle,
            );
        });
    }

    override clipElement(context: CanvasRenderingContext2D, elementIndex: number) {
        const path = new Path2D();
        const circlePath = new Path2D();
        const [position, radius] = this._applyAnimationToIndex(elementIndex);
        const radiusIncludingStroke = radius + this.lineWidth / 2;
        circlePath.arc(position.x, position.y, radiusIncludingStroke, 0, 2 * Math.PI);
        path.addPath(circlePath);
        context.clip(path);
    }

    /** Apply the animation to the bounds at the given index. This will return a new rectangle and fill color. */
    _applyAnimationToIndex(index: number): [Position, Pixels, RgbaString] {
        let tempRadius = this.radius;
        let { x, y } = this.#centerPositionsForLevel[index];
        let fill = this.fill;
        const [r, g, b] = string2rgbaArray(fill);
        this.animationConfigurations.forEach((animation) => {
            switch (animation.property) {
                case 'radius':
                    {
                        const offset = animation.shouldOffsetPropertyValue ? this.radius : 0;
                        tempRadius = (animation.computedValues[index] + offset) as Pixels;
                    }
                    break;
                case 'x':
                    {
                        const offset = animation.shouldOffsetPropertyValue ? x : 0;
                        x = animation.computedValues[index] + offset;
                    }
                    break;
                case 'y':
                    {
                        const offset = animation.shouldOffsetPropertyValue ? y : 0;
                        y = animation.computedValues[index] + offset;
                    }
                    break;
                case 'alpha':
                    fill = rgbArray2String([
                        r,
                        g,
                        b,
                        animation.computedValues[index],
                    ]) as RgbaString;
                    break;
            }
        });

        return [{ x, y }, this.#fitAnimatedRadius(tempRadius), fill];
    }

    override get _numberOfShapes(): number {
        return this.#centerPositionsForLevel.length;
    }

    override _getInferredValues(inferredDimension: DynamicInferredDimensions): number[] {
        switch (inferredDimension) {
            case DynamicInferredDimensions.height:
            case DynamicInferredDimensions.maxHeight:
            case DynamicInferredDimensions.width:
            case DynamicInferredDimensions.maxWidth:
                return Array(this._numberOfShapes).fill(2 * this.#authoredRadiusWithStroke());

            // The rest are not valid inferred dimensions when animating a circle
            case DynamicInferredDimensions.gapless:
            case DynamicInferredDimensions.heightWithoutDescent:
            case DynamicInferredDimensions.gaplessWidth:
            default:
                throw new Error(`Invalid inferred dimension ${inferredDimension}`);
        }
    }

    override _getOffsetForProperty(
        property: AnimatableCircleProperties,
        shouldOffsetPropertyValue: boolean,
    ): number {
        switch (property) {
            case 'x':
            case 'y':
            case 'radius':
                // offset is handled during draw
                return 0;
            case 'alpha':
                if (shouldOffsetPropertyValue) {
                    // During the canvas migration we didn't have any use cases of offsetting alpha for
                    // circles, so we're not adding support for it here. In general, it probably doesn't
                    // make sense to support offsetting alpha, so it would be nice to clean this up
                    // eventually, but for now just enforce that we can't try to use this combination.
                    throw new Error(`Property ${property} does not support offsetting.`);
                }
                return 0;
            default:
                throw new Error(`Unknown property ${property}`);
        }
    }
}

const drawCircle = (
    context: CanvasRenderingContext2D,
    params: CircleParameters,
    fillStyle: RgbaString | CanvasGradient,
    strokeStyle: RgbaString,
) => {
    const { x, y, lineWidth, radius } = params;
    context.fillStyle = fillStyle;
    context.strokeStyle = strokeStyle;
    context.lineWidth = lineWidth;
    context.beginPath();
    context.arc(x, y, radius, 0, 2 * Math.PI);
    context.fill();
    context.stroke();
};
